import React, { ReactElement, PropsWithChildren } from 'react';

import { Icon } from '@pb/shared/ui/atoms/icon';
import { TButtonIconProperties, TSize } from './button-icon.types';
import {
    StyledButtonIconWrapper,
    StyledButtonIcon,
} from './button-icon.styled';

/**
 * The Button component is used to output an `<a>` element if the "to" attribute
 * is present or an `<button>` element if it's not.
 * Both elements look the same but assistive technologies will let user's know
 * what to expect when clicking.
 */

export function setIconSize(buttonSize: TSize, iconSize: TSize): number {
    if (buttonSize === 'default' && iconSize === 'default') {
        return 24;
    }
    if (buttonSize === 'large' && iconSize === 'large') {
        return 48;
    }
    return 32;
}

export function ButtonIcon({
    icon,
    to,
    target = '_self',
    color = 'purple',
    onClick,
    size = 'default',
    iconSize = 'default',
    isOpen = false,
    circled,
    isInverted,
    ariaLabel,
    className,
    id,
}: PropsWithChildren<TButtonIconProperties>): ReactElement {
    const iconElement = (
        <Icon
            type={icon}
            color={color}
            circled={circled}
            size={setIconSize(size, iconSize)}
        />
    );

    if (
        to &&
        (to.indexOf('http') === 0 ||
            to.indexOf('tel:') === 0 ||
            to.indexOf('mailto:') === 0)
    ) {
        return (
            <StyledButtonIcon
                as="a"
                href={to}
                target={target}
                rel="noreferrer noopener"
                size={size}
                color={color}
                isOpen={isOpen}
                isInverted={isInverted}
                aria-label={ariaLabel}
                id={id}
            >
                {iconElement}
            </StyledButtonIcon>
        );
    }
    return (
        <StyledButtonIconWrapper className={className}>
            <StyledButtonIcon
                role="button"
                size={size}
                color={color}
                isOpen={isOpen}
                onClick={onClick}
                isInverted={isInverted}
                aria-label={ariaLabel}
                id={id}
            >
                {iconElement}
            </StyledButtonIcon>
        </StyledButtonIconWrapper>
    );
}
